import axios from 'axios'
import { zynity_api } from '../../../config/external-routes'
import { assistFeatureLoading, assistFeatureResolved, itemInsightFeatureResolved } from './assistSlice'

const authHeader = (state: any) => {
    const token = state()?.auth?.access_token
    if (!token) return
    try {
        axios.defaults.headers = axios.defaults.headers || ({} as any)
        axios.defaults.headers.common = { 'Authorization': `Bearer ${token}` }
    } catch (error) {
        /* axios not fully initialised (tests) - requests still carry app-level auth */
    }
}

/**
 * Resolve (once) whether Zyni content assist is enabled for this user by reading
 * the dedicated `content_assist` capability the bootstrap payload carries -
 * independent of whether any chat domain is enabled, and present even on the
 * bootstrap endpoint's 403 branches (it lives under `meta` there instead of
 * `features`). Safe to dispatch from every field mount.
 */
export const ensureAssistFeature = () => {
    return async (dispatch: (arg0: any) => void, state: any) => {
        const feature = state()?.engagement_assist?.feature
        if (feature?.checked || feature?.loading) {
            return
        }

        dispatch(assistFeatureLoading())
        authHeader(state)

        try {
            const response = await axios.get(zynity_api.engagement_ai_bootstrap)
            dispatch(assistFeatureResolved(response?.data?.features?.content_assist))
            dispatch(itemInsightFeatureResolved(response?.data?.features?.organizational_intelligence))
        } catch (error: any) {
            // Bootstrap 403s (e.g. no chat domain enabled) still carry content_assist
            // under meta - read it there rather than assuming "not available".
            dispatch(assistFeatureResolved(error?.response?.data?.meta?.content_assist))
            dispatch(itemInsightFeatureResolved(error?.response?.data?.meta?.organizational_intelligence))
        }
    }
}

export interface ItemInsightRequest {
    organization_id?: number,
    team_id: number,
    datum_id: number,
    message?: string,
}

/**
 * Ask Zyni about one Engage item (Organizational Intelligence Proof #1). Resolves
 * with the response body ({ insight, insight_id, ... }); rejects with the axios error
 * so the caller can surface error.response.data.error.message.
 */
export const requestItemInsight = (payload: ItemInsightRequest) => {
    return async (_dispatch: (arg0: any) => void, state: any) => {
        authHeader(state)

        const response = await axios.post(zynity_api.engagement_ai_oi_item_insight, payload)
        return response.data
    }
}

export interface FieldAssistRequest {
    surface: string,
    field_key: string,
    variant?: string,
    operation: 'generate' | 'revise',
    record_ref: Record<string, any>,
    field_value?: string,
    instruction?: string,
    history?: Array<{ role: 'user' | 'assistant', content: string }>,
    interaction_id?: number | null,
}

/**
 * Ask Zyni to draft or revise the content of one field. Resolves with the
 * response body ({ assist, interaction_id, ... }); rejects with the axios error
 * so the caller can surface error.response.data.error.message.
 */
export const requestFieldAssist = (payload: FieldAssistRequest) => {
    return async (_dispatch: (arg0: any) => void, state: any) => {
        authHeader(state)

        const body: Record<string, any> = {
            surface: payload.surface,
            field_key: payload.field_key,
            operation: payload.operation,
            record_ref: payload.record_ref || {},
        }
        if (payload.variant) body.variant = payload.variant
        if (payload.field_value) body.field_value = payload.field_value
        if (payload.instruction) body.instruction = payload.instruction
        if (payload.history && payload.history.length > 0) body.history = payload.history
        if (payload.interaction_id) body.interaction_id = payload.interaction_id

        const response = await axios.post(zynity_api.engagement_ai_assist, body)
        return response.data
    }
}
